import React, { useCallback, useEffect, useState } from "react";
import { logWarn, logError } from "../lib/logger";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Bell, BellRing, RefreshCw, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch (e) {
      logWarn("notifications load failed", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openNotif = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); }
      catch (err) { logError("Failed to mark notification read:", err); }
      load();
    }
    if (n.job_id) navigate(`/jobs/${n.job_id}`);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const checkNow = async () => {
    setScanning(true);
    try {
      const r = await api.post("/notifications/scan");
      await load();
      toast.success(r.data.created ? `${r.data.created} new reminder${r.data.created > 1 ? "s" : ""}` : "You're all caught up");
    } catch {
      toast.error("Could not check now");
    } finally {
      setScanning(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        data-testid="notif-bell-trigger"
        className="relative flex items-center justify-center w-9 h-9 border border-transparent hover:border-zinc-200 transition-colors"
        aria-label="Notifications"
      >
        {unread > 0 ? <BellRing size={18} /> : <Bell size={18} className="text-zinc-500" />}
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="rounded-none w-80 p-0 border-zinc-200" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <span className="label-tiny">Notifications</span>
          <div className="flex items-center gap-2">
            <button onClick={checkNow} disabled={scanning} data-testid="notif-check-now" className="text-zinc-500 hover:text-zinc-900 disabled:opacity-50" title="Check now">
              <RefreshCw size={14} className={scanning ? "animate-spin" : ""} />
            </button>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all" className="text-zinc-500 hover:text-zinc-900" title="Mark all read">
                <CheckCheck size={15} />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-80 overflow-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="notif-empty">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => openNotif(n)}
                data-testid={`notif-item-${n.notification_id}`}
                className={"w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors flex gap-3 " + (n.read ? "" : "bg-amber-50/60")}
              >
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 shrink-0" />}
                <div className={n.read ? "pl-[18px]" : ""}>
                  <div className="text-sm font-medium leading-snug">{n.title}</div>
                  <div className="text-xs text-zinc-600 mt-0.5 leading-snug">{n.body}</div>
                  <div className="font-mono-jb text-[10px] text-zinc-400 mt-1">{timeAgo(n.created_at)}</div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
